
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { User, Session, Provider } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { generateCSRFToken } from "@/utils/security";
import { toast } from "sonner";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isDevMockUser: boolean;
  csrfToken: string;
  refreshCSRFToken: () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, username?: string) => Promise<void>;
  signOut: () => Promise<void>;
  signInWithProvider: (provider: Provider) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Check if we're in development mode
const isDevelopment = import.meta.env.DEV;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [csrfToken, setCsrfToken] = useState(() => generateCSRFToken());
  
  // Check if using development mock user
  const isDevMockUser = isDevelopment && user === null && !isLoading;

  const refreshCSRFToken = () => {
    const newToken = generateCSRFToken();
    setCsrfToken(newToken);
    sessionStorage.setItem('csrf_token', newToken);
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        toast.error(error.message);
        throw error;
      }
      
      toast.success("Signed in successfully!");
    } catch (error) {
      console.error("Sign in error:", error);
      throw error;
    }
  };

  const signUp = async (email: string, password: string, username?: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: username ? { username } : undefined,
        },
      });
      
      if (error) {
        toast.error(error.message);
        throw error;
      }
      
      toast.success("Please check your email to confirm your account!");
    } catch (error) {
      console.error("Sign up error:", error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      // Clear sensitive data before signing out
      localStorage.removeItem('last_login');
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('rate_limit_')) {
          localStorage.removeItem(key);
        }
      });
      sessionStorage.clear();
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        toast.error(error.message);
        throw error;
      }
      
      toast.success("Signed out successfully!");
    } catch (error) {
      console.error("Sign out error:", error);
      throw error;
    }
  };

  const signInWithProvider = async (provider: Provider) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: redirectUrl,
        },
      });
      
      if (error) {
        toast.error(error.message);
        throw error;
      }
    } catch (error) {
      console.error(`${provider} sign in error:`, error);
      throw error;
    }
  };

  useEffect(() => {
    // Initialize CSRF token in session storage
    sessionStorage.setItem('csrf_token', csrfToken);

    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error getting session:", error);
        } else {
          setSession(initialSession);
          setUser(initialSession?.user ?? null);
        }
      } catch (error) {
        console.error("Error in getInitialSession:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("Auth state changed:", event, session?.user?.email);
        
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);

        // Security: Refresh CSRF token on auth state changes
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
          refreshCSRFToken();
        }

        // Security: Clear sensitive data on sign out
        if (event === 'SIGNED_OUT') {
          localStorage.removeItem('last_login');
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('rate_limit_')) {
              localStorage.removeItem(key);
            }
          });
        }

        // Security: Log successful sign in
        if (event === 'SIGNED_IN' && session?.user) {
          localStorage.setItem('last_login', Date.now().toString());
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [csrfToken]);

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    isDevMockUser,
    csrfToken,
    refreshCSRFToken,
    signIn,
    signUp,
    signOut,
    signInWithProvider
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
